import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import { Button } from '../ui/button';
import { VendorCompliance } from '../../types';
import { getComplianceStatusClasses } from '../../utils/vendorCompliance';

interface ComplianceOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  compliance: VendorCompliance | null;
  onConfirm: (reason: string) => void;
  isLoading?: boolean;
}

const ComplianceOverrideModal: React.FC<ComplianceOverrideModalProps> = ({
  isOpen,
  onClose,
  compliance,
  onConfirm,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = () => {
    if (!reason.trim()) return;
    onConfirm(reason.trim());
  };

  if (!compliance) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Vendor Compliance Warning"
      maxWidth="2xl"
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {compliance.vendorName} is {compliance.complianceStatusLabel}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                You can still assign this vendor, but you must document why you are proceeding.
              </p>
            </div>
          </div>
        </div>

        <div>
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getComplianceStatusClasses(compliance.complianceStatus, compliance.complianceStatusLabel)}`}>
            {compliance.complianceStatusLabel}
          </span>
        </div>

        {compliance.issues.length > 0 && (
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            {compliance.issues.map((issue) => (
              <li key={`${issue.code}-${issue.policyId ?? 'general'}`} className="flex items-start gap-2">
                <AlertTriangle className={`h-4 w-4 mt-0.5 ${issue.severity === 'Blocking' ? 'text-red-500' : 'text-yellow-500'}`} />
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        )}

        <div>
          <label htmlFor="compliance-override-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Override Reason
          </label>
          <textarea
            id="compliance-override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Explain why you are dispatching this vendor despite the compliance issue..."
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading || !reason.trim()}
          >
            {isLoading ? 'Assigning...' : 'Assign Vendor Anyway'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ComplianceOverrideModal;
